import { useState } from "react";
import { useCountdownAction } from "@/hooks/useCountdownAction";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Upload, X } from "lucide-react";

// Restore guard: type RESTORE -> 30 s cancellable countdown -> restore.
export default function RestoreBackupDialog({ backup, onClose, onRestore }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fire = async () => {
    setBusy(true);
    try { await onRestore(backup.id); onClose(); } finally { setBusy(false); }
  };
  const { countdown, start, cancel } = useCountdownAction(fire);
  const close = () => { cancel(); setText(""); onClose(); };

  return (
    <Dialog open={!!backup} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent data-testid="restore-backup-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-amber-700"><AlertTriangle className="h-5 w-5" /> Restore Backup</DialogTitle>
          <DialogDescription>
            This restores records from <span className="font-mono-data">{backup?.filename}</span> over your live data. Existing records with the same id are kept. This can't be undone.
          </DialogDescription>
        </DialogHeader>
        {countdown === null ? (
          <div className="space-y-2">
            <Label className="text-sm">Type <span className="font-mono-data font-semibold text-amber-600">RESTORE</span> to confirm</Label>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="RESTORE" autoFocus data-testid="restore-confirm-input" />
          </div>
        ) : (
          <div className="text-center py-4" data-testid="restore-countdown">
            <div className="text-5xl font-bold font-mono-data text-amber-600 tabular-nums">{countdown}</div>
            <p className="text-sm text-slate-500 mt-2">{busy ? "Restoring…" : "Restore starts in a moment. Press Cancel to stop."}</p>
          </div>
        )}
        <DialogFooter>
          {countdown === null ? (
            <>
              <Button variant="outline" onClick={close} data-testid="restore-cancel">Cancel</Button>
              <Button className="bg-amber-600 hover:bg-amber-700" disabled={text.trim().toUpperCase() !== "RESTORE"} onClick={start} data-testid="restore-start-btn">
                <Upload className="h-4 w-4 mr-1.5" /> Restore Now
              </Button>
            </>
          ) : (
            <Button variant="outline" className="w-full border-slate-300" disabled={busy} onClick={cancel} data-testid="restore-cancel-countdown">
              <X className="h-4 w-4 mr-1.5" /> Cancel Restore
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
